import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

/**
 * Authenticated API Client Singleton
 * Automatically adds authentication tokens to all requests
 * Note: This is a secondary client for provider-specific operations
 * Use 'client.ts' for main app authentication
 */





class ApiClient {
    private static instances: Map<string, ApiClient> = new Map();
    private axiosInstance: AxiosInstance;
    private accessToken: string | null = null;
    private refreshToken: string | null = null;

    private constructor(baseURL: string) {
        this.axiosInstance = axios.create({
            baseURL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Request interceptor to add auth token
        this.axiosInstance.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                // First, try to get token from localStorage (main app token)
                const appToken = localStorage.getItem('auth_token');

                if (appToken && config.headers) {
                    config.headers.Authorization = `Bearer ${appToken}`;
                } else if (this.accessToken && config.headers) {
                    // Fallback to manually set token
                    config.headers.Authorization = `Bearer ${this.accessToken}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor for error handling and token refresh
        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // If error is 401 and we haven't tried to refresh yet
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        // Attempt to refresh the token
                        const newAccessToken = await this.refreshAccessToken();

                        if (newAccessToken) {
                            // Update the authorization header with new token
                            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                            // Retry the original request
                            return this.axiosInstance(originalRequest);
                        }
                    } catch (refreshError) {
                        // Refresh failed - clear tokens and reject
                        this.clearToken();
                        console.error('[ProviderApiClient] Token refresh failed:', refreshError);
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    /**
     * Refresh access token using refresh token
     */
    private async refreshAccessToken(): Promise<string | null> {
        const refreshToken = this.refreshToken || localStorage.getItem('refresh_token');

        if (!refreshToken) {
            console.error('[ProviderApiClient] No refresh token available');
            return null;
        }

        try {
            console.log('[ProviderApiClient] Refreshing access token...');

            // Call refresh endpoint (without interceptor to avoid infinite loop)
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
                { refreshToken },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            const { accessToken, refreshToken: newRefreshToken } = response.data;

            if (accessToken) {
                // Update tokens
                this.setTokens(accessToken, newRefreshToken || refreshToken);
                console.log('[ProviderApiClient] Access token refreshed successfully');
                return accessToken;
            }

            return null;
        } catch (error) {
            console.error('[ProviderApiClient] Error refreshing token:', error);
            throw error;
        }
    }

    /**
     * Get singleton instance for a specific baseURL
     */
    public static getInstance(baseURL: string): ApiClient {
        if (!ApiClient.instances.has(baseURL)) {
            ApiClient.instances.set(baseURL, new ApiClient(baseURL));
        }
        return ApiClient.instances.get(baseURL)!;
    }

    /**
     * Set authentication token (optional, uses localStorage by default)
     */
    public setTokens(accessToken: string, refreshToken: string): void {
        this.accessToken = accessToken;
        this.refreshToken = refreshToken;
        localStorage.setItem('auth_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        console.log('[ProviderApiClient] Tokens set successfully', { accessToken, refreshToken });
    }

    /**
     * Clear authentication token
     */
    public clearToken(): void {
        this.accessToken = null;
    }

    /**
     * Get current token
     */
    public getTokens(): { accessToken: string | null; refreshToken: string | null } {
        return {
            accessToken: this.accessToken || localStorage.getItem('auth_token'),
            refreshToken: this.refreshToken || localStorage.getItem('refresh_token')
        };
    }

    /**
     * Check if client has a token
     */
    public hasToken(): boolean {
        const tokens = this.getTokens();
        return tokens.accessToken !== null && tokens.refreshToken !== null;
    }

    /**
     * Make GET request
     */
    public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.get<T>(url, config);
        return response.data;
    }

    /**
     * Make POST request
     */
    public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.post<T>(url, data, config);
        return response.data;
    }

    /**
     * Make PUT request
     */
    public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.put<T>(url, data, config);
        return response.data;
    }

    /**
     * Make PATCH request
     */
    public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.patch<T>(url, data, config);
        return response.data;
    }

    /**
     * Make DELETE request
     */
    public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.delete<T>(url, config);
        return response.data;
    }

    /**
     * Get raw axios instance for advanced usage
     */
    public getAxiosInstance(): AxiosInstance {
        return this.axiosInstance;
    }
}

const apiBaseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';
const analyseBaseURL = import.meta.env.VITE_ANALYSE_BASE_URL || 'http://localhost:2343';

// Export singleton instance
export const apiClient = ApiClient.getInstance(apiBaseURL);
export const analyseClient = ApiClient.getInstance(analyseBaseURL);

// Export class for type usage
export default apiClient;
